/**
 * Site and Geofence Data Transformation Utilities
 *
 * This module provides utilities to transform site and geofence data
 * into formats optimized for DeckGL layer rendering.
 *
 * The transformation separates the heavy React component creation (popovers)
 * from the lightweight data objects needed for GPU rendering, which is key
 * to achieving high performance with many markers.
 */

import { GeofenceType, WaypointType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { LiveSiteData } from '~hooks/useLiveMap/models';
import { Geofence, SiteBasic } from '~hooks/useSites/models';
import { Nullable } from '~types/Nullable';

import { SiteIconType } from '../siteIconAtlas';

/**
 * RGBA color tuple for DeckGL layers.
 * Values are in range 0-255.
 */
export type RGBAColor = [number, number, number, number];

/**
 * Data structure for a single site marker in the DeckGL IconLayer.
 * This is a plain object (not a React component) for efficient GPU rendering.
 */
export interface SiteLayerDatum {
  /** Unique identifier for the site */
  id: string;
  /** Position as [longitude, latitude] for DeckGL */
  position: [number, number];
  /** Icon type to render from the atlas */
  icon: SiteIconType;
  /** Waypoint type for the site */
  waypointType: WaypointType | 'both';
  /** Whether this site is a default (company/project) site — rendered with a badge. */
  isDefaultSite: boolean;
  /**
   * Full site data for popover rendering on-demand.
   * This data is NOT rendered immediately - only when user interacts.
   */
  siteData: LiveSiteData;
}

/**
 * Data structure for a circle geofence in the DeckGL ScatterplotLayer.
 */
export interface CircleGeofenceDatum {
  /** Unique identifier for the geofence */
  id: string;
  /** Parent site ID */
  siteId: string;
  /** Parent site name */
  siteName: string;
  /** Full site data for popover rendering */
  site: SiteBasic;
  /** Center position as [longitude, latitude] for DeckGL */
  center: [number, number];
  /** Radius in meters */
  radiusMeters: number;
  /** Fill color as RGBA (0-255) */
  fillColor: RGBAColor;
  /** Stroke color as RGBA (0-255) */
  strokeColor: RGBAColor;
  /** Whether this is the primary geofence (vs additional) */
  isPrimary: boolean;
  /** Tag for additional geofences (loading, unloading, etc.) */
  tag?: string;
  /** Waypoint type for primary geofences */
  waypointType?: WaypointType | 'both';
  /** Whether the parent site is a default (candidate) site. */
  isDefaultSite?: boolean;
  /** Whether this is a moving site geofence */
  isMovingSite: boolean;
}

/**
 * Data structure for a polygon geofence in the DeckGL GeoJsonLayer.
 */
export interface PolygonGeofenceDatum {
  /** Unique identifier for the geofence */
  id: string;
  /** Parent site ID */
  siteId: string;
  /** Parent site name */
  siteName: string;
  /** Full site data for popover rendering */
  site: SiteBasic;
  /** GeoJSON polygon coordinates [[[lng, lat], ...]] */
  coordinates: number[][][];
  /** Fill color as RGBA (0-255) */
  fillColor: RGBAColor;
  /** Stroke color as RGBA (0-255) */
  strokeColor: RGBAColor;
  /** Whether this is the primary geofence (vs additional) */
  isPrimary: boolean;
  /** Tag for additional geofences (loading, unloading, etc.) */
  tag?: string;
  /** Waypoint type for primary geofences */
  waypointType?: WaypointType | 'both';
  /** Whether the parent site is a default (candidate) site. */
  isDefaultSite?: boolean;
}

/**
 * Geofence color palette based on brandV2 theme.
 * Colors are defined as RGBA arrays for DeckGL.
 *
 * Opacity values:
 * - Fill opacity: 25% (calculated as 0.25 * 255 = 64)
 * - Stroke opacity: 100% (255/255)
 */
const GEOFENCE_COLORS = {
  // Primary geofence colors (static sites)
  // Blue (#0000FF) with 25% fill opacity (64 = 0.25 * 255)
  primaryFill: [0, 0, 255, 64] as RGBAColor,
  primaryStroke: [0, 0, 255, 255] as RGBAColor,

  // Moving site geofence colors
  // Black (#000000) with 25% fill opacity (64 = 0.25 * 255)
  movingSiteFill: [0, 0, 0, 64] as RGBAColor,
  movingSiteStroke: [0, 0, 0, 255] as RGBAColor,

  // Additional geofence tag colors based on brandV2 palette
  // treadGreen (#00A11A) with 25% fill opacity (64 = 0.25 * 255)
  loadingFill: [0, 161, 26, 64] as RGBAColor,
  loadingStroke: [0, 161, 26, 255] as RGBAColor,
  // treadOrangeDark (#FF7A00) with 25% fill opacity (64 = 0.25 * 255)
  unloadingFill: [255, 122, 0, 64] as RGBAColor,
  unloadingStroke: [255, 122, 0, 255] as RGBAColor,
  // treadBlue2 (#2196F3) with 25% fill opacity (64 = 0.25 * 255)
  checkInFill: [33, 150, 243, 64] as RGBAColor,
  checkInStroke: [33, 150, 243, 255] as RGBAColor,
  // treadYellowDark (#ECC600) with 25% fill opacity (64 = 0.25 * 255)
  scalingFill: [236, 198, 0, 64] as RGBAColor,
  scalingStroke: [236, 198, 0, 255] as RGBAColor,
  // treadRed (#EA1200) with 25% fill opacity (64 = 0.25 * 255)
  ticketingFill: [234, 18, 0, 64] as RGBAColor,
  ticketingStroke: [234, 18, 0, 255] as RGBAColor,
  // Fallback gray for unknown tags (treadGray3 #697982) with 25% fill opacity (64 = 0.25 * 255)
  fallbackFill: [105, 121, 130, 64] as RGBAColor,
  fallbackStroke: [105, 121, 130, 255] as RGBAColor,
} as const;

/**
 * Hover state opacity increase.
 * Fill opacity: 25% -> 40% (calculated as 0.40 * 255 = 102)
 */
export const GEOFENCE_HOVER_FILL_OPACITY = 102;

/**
 * Stroke weight constants (in pixels).
 */
export const GEOFENCE_STROKE_WEIGHT = 2;
export const GEOFENCE_ADDITIONAL_STROKE_WEIGHT = 3;
export const GEOFENCE_HOVER_STROKE_WEIGHT = 4;

/**
 * Gets the fill and stroke colors for a geofence tag.
 *
 * @param tag - The geofence tag (loading, unloading, check_in, scaling, ticketing)
 * @param isMovingSite - Whether this is a moving site geofence
 * @returns Object with fill and stroke colors
 */
const getGeofenceTagColors = (
  tag: Nullable<string>,
  isMovingSite: boolean = false,
): { fill: RGBAColor; stroke: RGBAColor } => {
  if (isMovingSite) {
    return {
      fill: GEOFENCE_COLORS.movingSiteFill,
      stroke: GEOFENCE_COLORS.movingSiteStroke,
    };
  }

  switch (tag?.toLowerCase()) {
    case 'loading':
      return {
        fill: GEOFENCE_COLORS.loadingFill,
        stroke: GEOFENCE_COLORS.loadingStroke,
      };
    case 'unloading':
      return {
        fill: GEOFENCE_COLORS.unloadingFill,
        stroke: GEOFENCE_COLORS.unloadingStroke,
      };
    case 'check_in':
      return {
        fill: GEOFENCE_COLORS.checkInFill,
        stroke: GEOFENCE_COLORS.checkInStroke,
      };
    case 'scaling':
      return {
        fill: GEOFENCE_COLORS.scalingFill,
        stroke: GEOFENCE_COLORS.scalingStroke,
      };
    case 'ticketing':
      return {
        fill: GEOFENCE_COLORS.ticketingFill,
        stroke: GEOFENCE_COLORS.ticketingStroke,
      };
    default:
      // Primary geofences (no tag) or unknown tags
      if (!tag) {
        return {
          fill: GEOFENCE_COLORS.primaryFill,
          stroke: GEOFENCE_COLORS.primaryStroke,
        };
      }
      return {
        fill: GEOFENCE_COLORS.fallbackFill,
        stroke: GEOFENCE_COLORS.fallbackStroke,
      };
  }
};

/**
 * Determines the site icon type based on waypoint type and state.
 *
 * @param waypointType - The waypoint type (pickup, drop_off, both)
 * @param isActive - Whether the site is currently hovered/selected
 * @param isDefaultSite - Whether the site is a default (candidate) site; renders
 *   the muted + dashed-ring variant (takes precedence over the active variant)
 * @returns The icon type to use from the atlas
 */
const getSiteIconType = (
  waypointType: WaypointType | 'both',
  isActive: boolean,
  isDefaultSite: boolean,
): SiteIconType => {
  if (waypointType === WaypointType.PICKUP) {
    if (isDefaultSite) return 'pickup_site_default';
    return isActive ? 'pickup_site_active' : 'pickup_site';
  }
  if (waypointType === WaypointType.DROP_OFF) {
    if (isDefaultSite) return 'dropoff_site_default';
    return isActive ? 'dropoff_site_active' : 'dropoff_site';
  }
  if (waypointType === WaypointType.STAGING) {
    if (isDefaultSite) return 'staging_site_default';
    return isActive ? 'staging_site_active' : 'staging_site';
  }
  // both or unknown
  if (isDefaultSite) return 'both_site_default';
  return isActive ? 'both_site_active' : 'both_site';
};

/**
 * Validates that a site has valid coordinates.
 *
 * @param lat - Latitude value
 * @param lon - Longitude value
 * @returns True if coordinates are valid finite numbers
 */
const isValidCoordinates = (lat: Nullable<number>, lon: Nullable<number>): boolean => {
  return lat !== null && lon !== null && Number.isFinite(lat) && Number.isFinite(lon);
};

/**
 * Options for transforming site location data.
 */
interface TransformSiteDataOptions {
  /** Currently active/selected marker ID (for styling) */
  activeMarkerId?: string | null;
}

/**
 * Transforms live site data into DeckGL IconLayer data.
 *
 * @param siteMarkersById - Map of site data keyed by site ID
 * @param options - Transform options for filtering and display
 * @returns Array of SiteLayerDatum objects ready for DeckGL
 */
export const transformSiteLocations = (
  siteMarkersById: Record<string, LiveSiteData>,
  options: TransformSiteDataOptions = {},
): SiteLayerDatum[] => {
  const { activeMarkerId } = options;

  return Object.values(siteMarkersById)
    .filter((siteData) => {
      // Skip sites with invalid coordinates
      const lat = siteData.site.lat;
      const lon = siteData.site.lon;
      return isValidCoordinates(lat, lon);
    })
    .filter((siteData) => {
      // Skip moving sites - they are rendered separately with truck layer
      return !siteData.site.nextBillionGeofence?.movingGeofence;
    })
    .map((siteData): SiteLayerDatum => {
      const lat = siteData.site.lat!;
      const lon = siteData.site.lon!;
      const isActive = siteData.site.id === activeMarkerId;

      return {
        id: siteData.site.id,
        position: [lon, lat], // DeckGL uses [lng, lat] order
        icon: getSiteIconType(siteData.waypointType, isActive, siteData.isDefaultSite),
        waypointType: siteData.waypointType,
        isDefaultSite: siteData.isDefaultSite,
        siteData,
      };
    });
};

/**
 * Extracts circle geofence data from site markers.
 *
 * @param siteMarkersById - Map of site data keyed by site ID
 * @param additionalGeofencesBySiteId - Optional map of additional geofences by site ID
 * @returns Array of CircleGeofenceDatum objects ready for DeckGL
 */
export const extractCircleGeofences = (
  siteMarkersById: Record<string, LiveSiteData>,
  additionalGeofencesBySiteId: Record<string, Geofence[]> = {},
): CircleGeofenceDatum[] => {
  const geofences: CircleGeofenceDatum[] = [];

  Object.values(siteMarkersById).forEach((siteData) => {
    const geofence = siteData.site.nextBillionGeofence;
    const isMovingSite = geofence?.movingGeofence ?? false;

    // Skip moving sites - they are rendered via Google Maps Circle for real-time updates
    if (isMovingSite) {
      return;
    }

    // Extract primary circle geofence
    if (geofence?.geofenceType === GeofenceType.CIRCLE) {
      // Get circle center coordinates
      const centerLat = geofence.circleCenter?.lat ?? siteData.site.lat;
      const centerLon = geofence.circleCenter?.lon ?? siteData.site.lon;

      if (isValidCoordinates(centerLat, centerLon)) {
        const radius = geofence.circleRadius;
        if (radius && radius > 0) {
          const colors = getGeofenceTagColors(null, isMovingSite);

          geofences.push({
            id: `${siteData.site.id}-primary`,
            siteId: siteData.site.id,
            siteName: siteData.site.name,
            site: siteData.site,
            center: [centerLon!, centerLat!],
            radiusMeters: radius,
            fillColor: colors.fill,
            strokeColor: colors.stroke,
            isPrimary: true,
            waypointType: siteData.waypointType,
            isDefaultSite: siteData.isDefaultSite,
            isMovingSite,
          });
        }
      }
    }

    // Extract additional circle geofences for this site
    const additionalGeofences = additionalGeofencesBySiteId[siteData.site.id] || [];
    additionalGeofences.forEach((additionalGeofence, index) => {
      // Skip non-circle geofences (type is a lowercased string: 'circle', 'polygon')
      if (additionalGeofence.type !== 'circle') {
        return;
      }

      const centerLat = additionalGeofence.circleCenter?.lat ?? siteData.site.lat;
      const centerLon = additionalGeofence.circleCenter?.lon ?? siteData.site.lon;

      if (!isValidCoordinates(centerLat, centerLon)) {
        return;
      }

      const radius = additionalGeofence.circleRadius;
      if (!radius || radius <= 0) {
        return;
      }

      const colors = getGeofenceTagColors(additionalGeofence.tag, false);

      geofences.push({
        id: `${siteData.site.id}-additional-${index}`,
        siteId: siteData.site.id,
        siteName: siteData.site.name,
        site: siteData.site,
        center: [centerLon!, centerLat!],
        radiusMeters: radius,
        fillColor: colors.fill,
        strokeColor: colors.stroke,
        isPrimary: false,
        tag: additionalGeofence.tag ?? undefined,
        waypointType: siteData.waypointType,
        isDefaultSite: siteData.isDefaultSite,
        isMovingSite: false,
      });
    });
  });

  // Sort by radius descending so smaller geofences render on top
  return geofences.sort((a, b) => b.radiusMeters - a.radiusMeters);
};

/**
 * Extracts polygon geofence data from site markers.
 *
 * @param siteMarkersById - Map of site data keyed by site ID
 * @param additionalGeofencesBySiteId - Optional map of additional geofences by site ID
 * @returns Array of PolygonGeofenceDatum objects ready for DeckGL
 */
export const extractPolygonGeofences = (
  siteMarkersById: Record<string, LiveSiteData>,
  additionalGeofencesBySiteId: Record<string, Geofence[]> = {},
): PolygonGeofenceDatum[] => {
  const geofences: PolygonGeofenceDatum[] = [];

  Object.values(siteMarkersById).forEach((siteData) => {
    const geofence = siteData.site.nextBillionGeofence;

    // Skip moving sites (handled separately)
    if (geofence?.movingGeofence) {
      return;
    }

    // Extract primary polygon geofence
    if (geofence?.geofenceType === GeofenceType.POLYGON) {
      const rawCoordinates = geofence.geojson?.coordinates;
      if (rawCoordinates && Array.isArray(rawCoordinates) && rawCoordinates.length > 0) {
        // Cast to proper polygon coordinates type after validation
        const coordinates = rawCoordinates as number[][][];

        // Validate polygon has at least 3 points in the first ring
        const firstRing = coordinates[0];
        if (firstRing && Array.isArray(firstRing) && firstRing.length >= 3) {
          const colors = getGeofenceTagColors(null, false);

          geofences.push({
            id: `${siteData.site.id}-primary`,
            siteId: siteData.site.id,
            siteName: siteData.site.name,
            site: siteData.site,
            coordinates,
            fillColor: colors.fill,
            strokeColor: colors.stroke,
            isPrimary: true,
            waypointType: siteData.waypointType,
            isDefaultSite: siteData.isDefaultSite,
          });
        }
      }
    }

    // Extract additional polygon geofences for this site
    const additionalGeofences = additionalGeofencesBySiteId[siteData.site.id] || [];
    additionalGeofences.forEach((additionalGeofence, index) => {
      // Skip non-polygon geofences (type is a lowercased string: 'circle', 'polygon')
      if (additionalGeofence.type !== 'polygon') {
        return;
      }

      const rawCoordinates = additionalGeofence.geojson?.coordinates;
      if (
        !rawCoordinates ||
        !Array.isArray(rawCoordinates) ||
        rawCoordinates.length === 0
      ) {
        return;
      }

      // Cast to proper polygon coordinates type after validation
      const coordinates = rawCoordinates as number[][][];

      // Validate polygon has at least 3 points in the first ring
      const firstRing = coordinates[0];
      if (!firstRing || !Array.isArray(firstRing) || firstRing.length < 3) {
        return;
      }

      const colors = getGeofenceTagColors(additionalGeofence.tag, false);

      geofences.push({
        id: `${siteData.site.id}-additional-${index}`,
        siteId: siteData.site.id,
        siteName: siteData.site.name,
        site: siteData.site,
        coordinates,
        fillColor: colors.fill,
        strokeColor: colors.stroke,
        isPrimary: false,
        tag: additionalGeofence.tag ?? undefined,
        waypointType: siteData.waypointType,
        isDefaultSite: siteData.isDefaultSite,
      });
    });
  });

  // Sort by area descending so smaller geofences render on top
  // Using bounding box area as approximation for simplicity
  return geofences.sort((a, b) => {
    const areaA = calculatePolygonBoundingBoxArea(a.coordinates);
    const areaB = calculatePolygonBoundingBoxArea(b.coordinates);
    return areaB - areaA;
  });
};

/**
 * Calculates approximate area of a polygon using its bounding box.
 * Used for sorting polygons by size.
 *
 * @param coordinates - GeoJSON polygon coordinates
 * @returns Approximate area (unitless, for comparison only)
 */
const calculatePolygonBoundingBoxArea = (coordinates: number[][][]): number => {
  if (!coordinates[0] || coordinates[0].length === 0) {
    return 0;
  }

  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  coordinates[0].forEach(([lng, lat]) => {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  });

  return (maxLng - minLng) * (maxLat - minLat);
};

/**
 * Formats a waypoint type for display in popovers.
 *
 * @param waypointType - The waypoint type
 * @returns Human-readable string
 */
export const formatWaypointType = (waypointType: WaypointType | 'both'): string => {
  switch (waypointType) {
    case WaypointType.PICKUP:
      return t('waypoint_type.pickup');
    case WaypointType.DROP_OFF:
      return t('waypoint_type.drop_off');
    case WaypointType.STAGING:
      return t('waypoint_type.staging');
    case 'both':
      return t('waypoint_type.both');
    default:
      return t('waypoint_type.site');
  }
};

/**
 * Formats a geofence tag for display in popovers.
 *
 * @param tag - The geofence tag
 * @returns Human-readable string with proper capitalization
 */
export const formatGeofenceTag = (tag: Nullable<string>): string => {
  if (!tag) {
    return t('geofence.primary');
  }

  // Convert snake_case to Title Case
  return tag
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Formats radius for display (in meters).
 *
 * @param radiusMeters - Radius in meters
 * @returns Formatted string with units
 */
export const formatRadius = (radiusMeters: number): string => {
  if (radiusMeters >= 1000) {
    return `${(radiusMeters / 1000).toFixed(1)}km`;
  }
  return `${Math.round(radiusMeters)}m`;
};
